// App cards in the chat stream, drawn natively from their declarative JSON (apps never send HTML). Three kinds:
// action (a form made from an action's inputs), data (a live list from a view) and result (what someone did, with
// buttons). Live parts read the app's records from the room's AppData, so a vote re-renders only its card.
// Every card reserves its height up front (see cardHeight in shared/src/apps.ts), so live data never shifts the list.
import { Fragment, useEffect, useRef, useState } from "react";
import { CARD_LIMITS, cardHeight, DEFAULT_MAX_FILES, fileRefs, formatViewValue, inputLabel, isHue, isTone, MAX_FILES, mediaKindOf, rankMark, stripCardMarkup, type AppManifest, type AppRecord, type Card, type CardButton, type FileRef, type InputSpec, type MediaKind, viewPill, viewRows } from "@bloom/shared";
import { useCollection, useRecord, type AppDataSource } from "../apps/data.ts";
import type { AppResult } from "../realtime.ts";
import { datetimeToSend, datetimeToShow } from "../datetimeField.ts";
import { fileUrl, formatBytes, INLINE_ORIGINAL_MAX, uploadFile, type UploadTask } from "../upload.ts";
import { BlockList, FieldRows, FootnoteLine, MiniChart, PeopleRow, Pill, ProgressRow, StatGrid, StatTile, TileGrid } from "../kit/blocks.tsx";
import { Avatar } from "./ui.tsx";
import { CloseButton } from "./icons.tsx";
import "../kit/blocks.css";
import "./apps.css";

export interface CardHost {
  /** The server (uploads for media inputs go to it). */
  serverId: string;
  source: AppDataSource;
  apps: AppManifest[];
  me: string | null;
  names: Map<string, string>;
  /** Members and channels for member/channel inputs on forms. */
  members: { id: string; name: string }[];
  channels: { id: string; name: string }[];
  call: (app: string, action: string, input: Record<string, unknown>) => Promise<AppResult>;
  open: (app: string, route?: Record<string, string>) => void;
  /** Drawn as a mockup (a blooming thread's card previews): forms don't grab the focus. */
  preview?: boolean;
}

/** Card text: *emphasis*, **bold**, <@id> as a name, \x as a literal x. Nothing else is markup. */
export function CardText({ text, names }: { text: string; names: Map<string, string> }) {
  const out: React.ReactNode[] = [];
  let buf = "";
  let i = 0;
  let k = 0;
  const flush = () => {
    if (buf) out.push(<Fragment key={k++}>{buf}</Fragment>);
    buf = "";
  };
  const readUntil = (end: string, from: number): [string, number] | null => {
    let s = "";
    for (let j = from; j < text.length; j++) {
      if (text[j] === "\\" && j + 1 < text.length) s += text[++j];
      else if (text.startsWith(end, j)) return [s, j + end.length];
      else s += text[j];
    }
    return null;
  };
  while (i < text.length) {
    const c = text[i]!;
    if (c === "\\" && i + 1 < text.length) {
      buf += text[i + 1];
      i += 2;
    } else if (text.startsWith("**", i) && readUntil("**", i + 2)) {
      const [inner, next] = readUntil("**", i + 2)!;
      flush();
      out.push(<strong key={k++}>{inner}</strong>);
      i = next;
    } else if (c === "*" && readUntil("*", i + 1)) {
      const [inner, next] = readUntil("*", i + 1)!;
      flush();
      out.push(<em key={k++} className="card-em">{inner}</em>);
      i = next;
    } else if (text.startsWith("<@", i) && text.indexOf(">", i) > i) {
      const end = text.indexOf(">", i);
      const id = text.slice(i + 2, end);
      flush();
      out.push(<strong key={k++} className="card-who">{names.get(id) ?? "Someone"}</strong>);
      i = end + 1;
    } else {
      buf += c;
      i++;
    }
  }
  flush();
  return <>{out}</>;
}

/** "via Feature requests", "· Bloom on behalf of Sam" when an AI made it, and "+N more" folded into it. */
function Via({ app, card, host }: { app?: AppManifest; card: Card; host: CardHost }) {
  const by = card.onBehalfOf;
  return (
    <div className="card-via">
      <span className="card-via-icon" aria-hidden="true">{app?.icon ?? "◆"}</span>
      <span>via {app?.name ?? "an app"}</span>
      {by && (
        <span className="card-agent" data-on-behalf-of={by.userId}>
          <span aria-hidden="true">·</span> <strong>{by.via}</strong> on behalf of {host.names.get(by.userId) ?? "someone"}
        </span>
      )}
      {!!card.more && (
        <button type="button" className="card-more" onClick={() => host.open(card.app)} title={`${card.more} more from ${app?.name ?? "this app"} weren't posted, to keep the channel readable. Open the app to see them.`}>
          +{card.more} more
        </button>
      )}
    </div>
  );
}

/** Opens a picture (or, with `video`, a video) in the chat's lightbox. */
export type OpenMedia = (src: string, original?: string, video?: boolean) => void;

/** A message's card (result or data). */
export function MessageCard({ card, host, onOpenMedia }: { card: Card; host: CardHost; onOpenMedia?: OpenMedia }) {
  const app = host.apps.find((a) => a.slug === card.app);
  if (card.view) return <DataCard card={card} app={app} host={host} />;
  return <ResultCard card={card} app={app} host={host} onOpenMedia={onOpenMedia} />;
}

/** Phone width, where stat tiles go 2 across (the same breakpoint as web/src/kit/blocks.css). */
export const narrowCards = () => typeof matchMedia === "function" && matchMedia("(max-width: 600px)").matches;

function ResultCard({ card, app, host, onOpenMedia }: { card: Card; app?: AppManifest; host: CardHost; onOpenMedia?: OpenMedia }) {
  const bound = card.bind ? <BoundButtons card={card} host={host} /> : <Buttons card={card} host={host} record={undefined} />;
  const tone = isTone(card.tone) ? card.tone : undefined;
  return (
    <div className="app-card result" style={{ minHeight: cardHeight(card, undefined, { narrow: narrowCards() }) - 12 }} data-card-kind="result" data-tone={tone} data-hue={appHue(app)}>
      <div className="card-line">
        {card.title && <strong className="card-title">{card.title} </strong>}
        {card.text && <CardText text={card.text} names={host.names} />}
        {card.meta?.map((m) => (
          <span key={m} className="card-meta">
            <span aria-hidden="true"> · </span>
            {m}
          </span>
        ))}
        {card.pills?.map((p, i) => (
          <Fragment key={i}>
            {" "}
            <Pill tone={p.tone} trend={p.trend} className="card-pill">{p.text}</Pill>
          </Fragment>
        ))}
      </div>
      {!!card.stats?.length && (
        <StatGrid count={card.stats.length} className="card-stats" label={card.title ?? "Stats"}>
          {card.stats.map((s, i) => <StatTile key={i} label={s.label} value={s.value} tone={s.tone} trend={s.trend} />)}
        </StatGrid>
      )}
      {!!card.fields?.length && <FieldRows className="card-fields-list" items={card.fields.map((f) => ({ label: f.label, value: f.value }))} />}
      {!!card.progress?.length && (
        <div className="card-progress">
          {card.progress.map((p, i) => <ProgressRow key={i} label={p.label} value={p.value} max={p.max} text={p.text} tone={p.tone} />)}
        </div>
      )}
      {card.chart && card.chart.points?.length >= 2 && <MiniChart type={card.chart.type} points={card.chart.points} tone={card.chart.tone} caption={card.chart.caption} format={card.chart.format} />}
      {!!card.grid?.rows?.length && <TileGrid rows={card.grid.rows} caption={card.grid.caption} label={card.title ?? (stripCardMarkup(card.text ?? "").replace(/<@[^>]+>/g, "").trim() || undefined)} />}
      {!!card.list?.items?.length && <BlockList style={card.list.style} items={card.list.items.map((it) => ({ ...it, text: <CardText text={it.text} names={host.names} /> }))} />}
      {card.people && <CardPeople card={card} host={host} />}
      {card.media && card.bind && <CardMedia card={card} host={host} onOpen={onOpenMedia} />}
      {bound}
      {card.footnote && <FootnoteLine className="card-footnote"><CardText text={card.footnote} names={host.names} /></FootnoteLine>}
      <Via app={app} card={card} host={host} />
    </div>
  );
}

/**
 * The bound record's files (card.media) as thumbnails, live (an edit that adds or removes one shows here). Display
 * copies first (shared/src/media.ts); a big picture without one is a tile with its name and size, never the original.
 * Its room is reserved (cardHeight), so the list never moves when they load.
 */
function CardMedia({ card, host, onOpen }: { card: Card; host: CardHost; onOpen?: OpenMedia }) {
  const record = useRecord(host.source, card.app, card.bind!.collection, card.bind!.id);
  const files = fileRefs(record?.data[card.media!]);
  if (card.mediaLayout === "hero") return <CardHero files={files} onOpen={onOpen} />;
  if (!files.length) return <div className="card-media" aria-hidden="true" />;
  return (
    <ul className="card-media" aria-label="Attachments">
      {files.slice(0, 6).map((f) => <CardThumb key={f.key} file={f} onOpen={onOpen} />)}
      {files.length > 6 && <li className="card-media-more">+{files.length - 6}</li>}
    </ul>
  );
}

/** The first file large (mediaLayout "hero"), at a fixed height; "+N" when there are more. */
function CardHero({ files, onOpen }: { files: FileRef[]; onOpen?: OpenMedia }) {
  const file = files[0];
  const url = file ? fileUrl(file.key) : "";
  const video = file ? mediaKindOf(file.type) === "video" : false;
  const sources = !file ? [] : video ? [`${url}/poster.jpg`] : [`${url}/w1200.webp`, ...(file.size <= INLINE_ORIGINAL_MAX ? [url] : [])];
  const [at, setAt] = useState(0);
  if (!file) return <div className="card-hero" aria-hidden="true" />;
  const src = sources[at];
  return (
    <button type="button" className="card-hero" onClick={() => (video ? onOpen?.(url, url, true) : onOpen?.(src ?? url, url))} title={file.name} aria-label={`Open ${file.name}`}>
      {src ? <img src={src} alt={file.name} loading="lazy" draggable={false} onError={() => setAt((n) => n + 1)} /> : <span className="card-thumb-name">{file.name} · {formatBytes(file.size)}</span>}
      {video && <span className="card-thumb-play" aria-hidden="true">▶</span>}
      {files.length > 1 && <span className="card-hero-more">+{files.length - 1}</span>}
    </button>
  );
}

/** Faces: a members field of the bound record (live), or the card's fixed ids. */
function CardPeople({ card, host }: { card: Card; host: CardHost }) {
  const p = card.people!;
  // Only a live row watches the record (fixed ids need nothing).
  const record = useRecord(p.field && card.bind ? host.source : null, card.app, card.bind?.collection ?? "", card.bind?.id ?? "");
  const live = p.field && card.bind ? record?.data[p.field] : undefined;
  const ids = (p.field ? (Array.isArray(live) ? live : []) : (p.ids ?? [])).filter((x): x is string => typeof x === "string");
  return <PeopleRow label={p.label} total={ids.length} shown={CARD_LIMITS.peopleShown} faces={ids.slice(0, CARD_LIMITS.peopleShown).map((id) => <Avatar key={id} id={id} name={host.names.get(id) ?? "?"} size={24} />)} />;
}

/** A record's field as row text: a member field as the person's name, a members list as its size, the rest as is. */
function fieldText(v: unknown, type: string | undefined, names: Map<string, string>): string {
  if (type === "member" && typeof v === "string") return names.get(v) ?? "Someone";
  if (Array.isArray(v)) return String(v.length);
  return v === undefined || v === null ? "" : String(v);
}

/** The app's own accent (look.accent), drawn by the card's [data-hue] (web/src/kit/blocks.css). */
export const appHue = (app?: AppManifest) => (isHue(app?.look?.accent) ? app!.look!.accent : undefined);

function CardThumb({ file, onOpen }: { file: FileRef; onOpen?: OpenMedia }) {
  const url = fileUrl(file.key);
  const video = mediaKindOf(file.type) === "video";
  // Try the small copy, then (a small enough picture) the original, then a tile.
  const sources = video ? [`${url}/poster.jpg`] : [`${url}/w400.webp`, ...(file.size <= INLINE_ORIGINAL_MAX ? [url] : [])];
  const [at, setAt] = useState(0);
  const src = sources[at];
  const open = () => {
    if (video) return onOpen?.(url, url, true);
    // The larger copy exists when the small one did; otherwise the original.
    onOpen?.(at === 0 && src ? `${url}/w1200.webp` : url, url);
  };
  return (
    <li>
      <button type="button" className={`card-thumb${src ? "" : " tile"}`} onClick={open} title={file.name} aria-label={`Open ${file.name}`}>
        {src ? <img src={src} alt={file.name} loading="lazy" draggable={false} onError={() => setAt((n) => n + 1)} /> : <span className="card-thumb-name">{file.name}<br />{formatBytes(file.size)}</span>}
        {video && <span className="card-thumb-play" aria-hidden="true">▶</span>}
      </button>
    </li>
  );
}

function BoundButtons({ card, host }: { card: Card; host: CardHost }) {
  const record = useRecord(host.source, card.app, card.bind!.collection, card.bind!.id);
  return <Buttons card={card} host={host} record={record} loaded={host.source.data.list(card.app, card.bind!.collection) !== undefined} />;
}

function Buttons({ card, host, record, loaded = true }: { card: Card; host: CardHost; record: AppRecord | undefined; loaded?: boolean }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  if (!card.buttons?.length) return null;
  const gone = card.bind && loaded && !record;
  const click = async (b: CardButton) => {
    if (b.kind === "open") return host.open(card.app, b.route);
    if (!b.action) return;
    setBusy(b.label);
    setError(null);
    const r = await host.call(card.app, b.action, b.input ?? {});
    setBusy(null);
    if (!r.ok) setError(r.message);
  };
  return (
    <div className="card-buttons">
      {card.buttons.map((b) => {
        const list = b.toggleField && record && Array.isArray(record.data[b.toggleField]) ? (record.data[b.toggleField] as string[]) : null;
        const on = !!list && !!host.me && list.includes(host.me);
        const disabled = (b.kind === "call" && (gone || busy !== null)) || false;
        return (
          <button
            key={b.label}
            type="button"
            className={`card-btn${b.primary ? " primary" : ""}${b.danger ? " danger" : ""}${on ? " on" : ""}`}
            aria-pressed={b.toggleField ? on : undefined}
            disabled={disabled}
            onClick={() => void click(b)}
          >
            {b.toggleField && !b.icon && <ArrowUp />}
            {b.icon && <span className="card-btn-icon" aria-hidden="true">{b.icon}</span>}
            {b.label}
            {b.toggleField && <span className="card-count" aria-label={`${list?.length ?? 0} votes`}>{list ? list.length : "–"}</span>}
          </button>
        );
      })}
      {gone && <span className="card-note">This was removed.</span>}
      {error && <span className="card-note error">{error}</span>}
    </div>
  );
}

function ArrowUp() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 19V5M5 12l7-7 7 7" />
    </svg>
  );
}

function DataCard({ card, app, host }: { card: Card; app?: AppManifest; host: CardHost }) {
  const view = app?.views?.[card.view!];
  const records = useCollection(host.source, card.app, view?.collection ?? "");
  const size = (r: AppRecord, f?: string) => {
    const v = f ? r.data[f] : undefined;
    return Array.isArray(v) ? v.length : typeof v === "number" ? v : 0;
  };
  const rows = view && records ? viewRows(records, view, app?.collections[view.collection]) : [];
  const fields = view ? app?.collections[view.collection]?.fields : undefined;
  return (
    <div className="app-card data" style={{ minHeight: cardHeight(card, app?.views) - 12 }} data-card-kind="data" data-hue={appHue(app)}>
      <div className="card-head">
        <strong className="card-title">{card.title ?? view?.title}</strong>
        <button type="button" className="card-btn small" onClick={() => host.open(card.app)}>Open</button>
      </div>
      <ol className={`card-list${view?.rank ? " ranked" : ""}`} style={{ minHeight: 34 * (view?.limit ?? 5) }}>
        {!records && <li className="card-list-empty">Loading…</li>}
        {records && !rows.length && <li className="card-list-empty">Nothing here yet.</li>}
        {rows.map((r, i) => (
          <li key={r.id}>
            <button type="button" className="card-row" onClick={() => host.open(card.app, { record: r.id })}>
              {view!.rank && <span className="card-row-rank" aria-label={`${i + 1}.`}>{rankMark(i)}</span>}
              <span className="card-row-title">{fieldText(r.data[view!.titleField], fields?.[view!.titleField]?.type, host.names)}</span>
              {view!.metaFields?.map((f) => {
                // A pill field that's missing reads as its default (a request from before statuses is Open).
                const v = r.data[f] ?? (view!.pills?.[f] ? app?.collections[view!.collection]?.fields[f]?.default : undefined);
                if (v === undefined) return null;
                // A field the view draws as a pill (ViewSpec.pills): formatted and toned, like the full view's badges.
                const pill = viewPill(view!, f, v);
                return pill ? <Pill key={f} tone={pill.tone} trend={pill.trend} className="card-row-pill">{pill.text}</Pill> : <span key={f} className="card-row-meta">{fieldText(v, fields?.[f]?.type, host.names)}</span>;
              })}
              {view!.valueField && typeof r.data[view!.valueField] === "number" && <span className="card-row-value">{formatViewValue(r.data[view!.valueField], view!.valueFormat ?? "number")}</span>}
              {view!.countField && !view!.valueField && (
                <span className={`card-row-count${host.me && (r.data[view!.countField] as string[] | undefined)?.includes(host.me) ? " on" : ""}`}>
                  <ArrowUp /> {size(r, view!.countField)}
                </span>
              )}
            </button>
          </li>
        ))}
      </ol>
      <Via app={app} card={card} host={host} />
    </div>
  );
}

/** A private card: only the person who ran the command sees it. Its form calls the action; then it shows "done". */
export function PrivateCardView({ id, card, host, onSubmit, onDismiss }: { id: string; card: Card; host: CardHost; onSubmit: (values: Record<string, unknown>) => Promise<AppResult>; onDismiss: () => void }) {
  const app = host.apps.find((a) => a.slug === card.app);
  return (
    <div className={`app-card private${card.done ? " done" : ""}${card.confirm && !card.done ? " confirm" : ""}`} data-private-card={id} role="group" aria-label={card.title ?? "App card"}>
      <div className="private-eyebrow">
        <EyeIcon />
        <span>Only you can see this</span>
        <span className="private-app">
          · {app?.icon} {app?.name}
        </span>
        <CloseButton size="sm" className="private-dismiss" aria-label="Dismiss" title="Dismiss" onClick={onDismiss} />
      </div>
      {card.done ? (
        <div className="private-done" role="status">
          <span className="done-check" aria-hidden="true">✓</span>
          <strong>{card.done.text.replace(/\s*✓$/, "")}</strong>
          {card.done.detail && <span className="muted"> · {card.done.detail}</span>}
          <button type="button" className="link-btn private-done-dismiss" onClick={onDismiss}>Dismiss</button>
        </div>
      ) : card.confirm ? (
        <ConfirmBody card={card} host={host} onConfirm={() => onSubmit({})} onCancel={onDismiss} />
      ) : card.form ? (
        <CardForm id={id} card={card} host={host} onSubmit={onSubmit} />
      ) : (
        card.text && <div className="card-line"><CardText text={card.text} names={host.names} /></div>
      )}
    </div>
  );
}

/**
 * An AI (Bloom) asked to do something destructive for me: what it wants, and the button that does it. The room runs
 * exactly what the card says (never what the tap sends), as me, attributed to the AI on my behalf.
 */
function ConfirmBody({ card, host, onConfirm, onCancel }: { card: Card; host: CardHost; onConfirm: () => Promise<AppResult>; onCancel: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const confirm = async () => {
    setBusy(true);
    setError(null);
    const r = await onConfirm();
    setBusy(false);
    if (!r.ok) setError(Object.values(r.errors ?? {})[0] ?? r.message);
  };
  return (
    <div className="card-confirm">
      {card.title && <div className="card-form-title">{card.title}</div>}
      {card.text && <div className="card-line"><CardText text={card.text} names={host.names} /></div>}
      <div className="card-form-foot">
        {error && <span className="card-error" role="alert">{error}</span>}
        <button type="button" className="btn btn-small card-cancel" onClick={onCancel} disabled={busy}>Cancel</button>
        <button type="button" className="btn btn-small btn-danger card-submit" onClick={() => void confirm()} disabled={busy}>{busy ? "Working…" : card.confirm!.label}</button>
      </div>
    </div>
  );
}

/** A form made from an action's inputs: text → a field, choice → a dropdown with its default, prefilled from the command. */
function CardForm({ id, card, host, onSubmit }: { id: string; card: Card; host: CardHost; onSubmit: (values: Record<string, unknown>) => Promise<AppResult> }) {
  const form = card.form!;
  const [values, setValues] = useState<Record<string, unknown>>(() => ({ ...form.values }));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState<Record<string, boolean>>({});
  const set = (k: string, v: unknown) => {
    setValues((x) => ({ ...x, [k]: v }));
    if (errors[k]) setErrors(({ [k]: _, ...rest }) => rest);
  };
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const missing: Record<string, string> = {};
    for (const f of form.fields) if (f.required && !(Array.isArray(values[f.key]) ? (values[f.key] as unknown[]).length : String(values[f.key] ?? "").trim())) missing[f.key] = `${inputLabel(f)} is required.`;
    if (Object.keys(missing).length) return setErrors(missing);
    if (Object.values(uploading).some(Boolean)) return setMessage("Wait for the uploads to finish.");
    setBusy(true);
    setMessage(null);
    // Media fields send their uploads' keys; the room looks each one up (name, type and size come from its records).
    // Date and time fields send an instant in UTC: the input's own value has no zone, and the room would read it as UTC (#251).
    const types = new Map(form.fields.map((f) => [f.key, f.type]));
    const send = (k: string, v: unknown) => (Array.isArray(v) ? fileRefs(v).map((x) => x.key) : types.get(k) === "datetime" && typeof v === "string" && v ? datetimeToSend(v) || v : v);
    const r = await onSubmit(Object.fromEntries(Object.entries(values).map(([k, v]) => [k, send(k, v)])));
    setBusy(false);
    if (!r.ok) {
      setErrors(r.errors ?? {});
      if (!r.errors || !Object.keys(r.errors).length) setMessage(r.message);
    }
  };
  // Short choices sit side by side (Category and Priority); text and long lists get a row each.
  const short = (f: InputSpec) => f.type === "choice" || f.type === "number" || f.type === "member" || f.type === "channel";
  return (
    <form className="card-form" onSubmit={submit} noValidate>
      {card.title && <div className="card-form-title">{card.title}</div>}
      <div className="card-fields">
        {form.fields.map((f) => f.type === "media" ? (
          <div key={f.key} className={`card-field${errors[f.key] ? " invalid" : ""}`} role="group" aria-labelledby={`${id}-${f.key}-label`}>
            <span className="card-label" id={`${id}-${f.key}-label`}>
              {inputLabel(f)}
              {f.required && <span className="req" aria-hidden="true"> *</span>}
            </span>
            <MediaField id={`${id}-${f.key}`} f={f} host={host} value={fileRefs(values[f.key])} onChange={(v) => set(f.key, v)} onBusy={(b) => setUploading((u) => ({ ...u, [f.key]: b }))} />
            {errors[f.key] && <span className="card-error" role="alert">{errors[f.key]}</span>}
          </div>
        ) : (
          <label key={f.key} className={`card-field${short(f) ? " half" : ""}${errors[f.key] ? " invalid" : ""}`}>
            <span className="card-label">
              {inputLabel(f)}
              {f.required && <span className="req" aria-hidden="true"> *</span>}
            </span>
            <FieldInput id={`${id}-${f.key}`} f={f} value={values[f.key]} onChange={(v) => set(f.key, v)} host={host} autoFocus={!host.preview && f.key === form.fields[0]?.key && !values[f.key]} />
            {errors[f.key] && <span className="card-error" role="alert">{errors[f.key]}</span>}
          </label>
        ))}
      </div>
      <div className="card-form-foot">
        {message && <span className="card-error" role="alert">{message}</span>}
        <button type="submit" className="btn btn-primary btn-small card-submit" disabled={busy}>{busy ? "Saving…" : form.submit}</button>
      </div>
    </form>
  );
}

function FieldInput({ id, f, value, onChange, host, autoFocus }: { id: string; f: InputSpec; value: unknown; onChange: (v: unknown) => void; host: CardHost; autoFocus?: boolean }) {
  const v = value === undefined || value === null ? "" : String(value);
  const common = { id, name: f.key, "aria-required": f.required || undefined, autoFocus };
  const options: { value: string; label: string }[] | null =
    f.type === "choice" ? (f.options ?? []).map((o) => ({ value: o, label: o }))
    : f.type === "record" ? (f.choices ?? [])
    : f.type === "member" ? host.members.map((m) => ({ value: m.id, label: m.name }))
    : f.type === "channel" ? host.channels.map((c) => ({ value: c.id, label: `#${c.name}` }))
    : null;
  if (options) {
    return (
      <span className="card-select">
        <select {...common} value={v} onChange={(e) => onChange(e.target.value)}>
          {(!f.default || f.type !== "choice") && <option value="">{f.type === "record" && !options.length ? "Nothing to pick yet" : "Choose…"}</option>}
          {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </span>
    );
  }
  if (f.multiline) return <textarea {...common} rows={3} maxLength={f.maxLength} placeholder={f.placeholder} value={v} onChange={(e) => onChange(e.target.value)} />;
  if (f.type === "number") return <input {...common} type="number" min={f.min} max={f.max} value={v} placeholder={f.placeholder} onChange={(e) => onChange(e.target.value)} />;
  if (f.type === "datetime") return <input {...common} type="datetime-local" value={datetimeToShow(value)} onChange={(e) => onChange(e.target.value)} />;
  return <input {...common} type="text" maxLength={f.maxLength} placeholder={f.placeholder} value={v} onChange={(e) => onChange(e.target.value)} autoComplete="off" />;
}

/**
 * A form's pictures-and-videos input: picks files (on phones, the photo library or the camera), uploads each through
 * the chat's pipeline (made smaller, hidden metadata removed, big ones in parts) with progress, and keeps the uploaded
 * files as the value. Nothing is attached until the form is sent; the room checks every file is the sender's.
 */
function MediaField({ id, f, host, value, onChange, onBusy }: { id: string; f: InputSpec; host: CardHost; value: FileRef[]; onChange: (v: FileRef[]) => void; onBusy: (busy: boolean) => void }) {
  const input = useRef<HTMLInputElement>(null);
  const [going, setGoing] = useState<{ id: string; name: string; preview: string; video: boolean; progress: number; task: UploadTask }[]>([]);
  const [error, setError] = useState<string | null>(null);
  const previews = useRef(new Map<string, string>());
  const latest = useRef(value);
  latest.current = value;
  // Files land in the order they were picked, whichever finishes uploading first.
  const order = useRef(new Map<string, number>());
  const picked = useRef(0);
  const inOrder = (list: FileRef[]) => [...list].sort((a, b) => (order.current.get(a.key) ?? -1) - (order.current.get(b.key) ?? -1));
  const kinds: MediaKind[] = f.accept?.length ? f.accept : ["image", "video"];
  const max = Math.min(f.maxFiles ?? DEFAULT_MAX_FILES, MAX_FILES);
  useEffect(() => onBusy(going.length > 0), [going.length]);
  useEffect(() => () => {
    for (const g of going) g.task.cancel();
  }, []);
  const pick = (files: FileList | null) => {
    setError(null);
    const list = [...(files ?? [])];
    if (input.current) input.current.value = "";
    const room = max - latest.current.length - going.length;
    if (list.length > room) setError(room > 0 ? `Up to ${max} files: added the first ${room}.` : `Up to ${max} files.`);
    for (const file of list.slice(0, Math.max(0, room))) {
      const kind = mediaKindOf(file.type);
      if (!kind || !kinds.includes(kind)) {
        setError(`${file.name} isn't ${kinds.length === 2 ? "a picture or a video" : kinds[0] === "video" ? "a video" : "a picture"}.`);
        continue;
      }
      const gid = Math.random().toString(36).slice(2);
      const n = picked.current++;
      const preview = URL.createObjectURL(file);
      const task = uploadFile(host.serverId, file, (p) => setGoing((g) => g.map((x) => (x.id === gid ? { ...x, progress: p } : x))));
      setGoing((g) => [...g, { id: gid, name: file.name, preview, video: kind === "video", progress: 0, task }]);
      task.done.then(
        (a) => {
          setGoing((g) => g.filter((x) => x.id !== gid));
          previews.current.set(a.key, preview);
          order.current.set(a.key, n);
          onChange(inOrder([...latest.current, { key: a.key, name: a.name, type: a.type, size: a.size, ...(a.width ? { width: a.width } : {}), ...(a.height ? { height: a.height } : {}) }]));
        },
        (e: unknown) => {
          setGoing((g) => g.filter((x) => x.id !== gid));
          URL.revokeObjectURL(preview);
          if (!(e instanceof Error && e.message === "Cancelled.")) setError(e instanceof Error ? e.message : "The upload failed.");
        },
      );
    }
  };
  return (
    <div className="card-media-field">
      {(value.length > 0 || going.length > 0) && (
        <ul className="card-media" aria-label={`${value.length} attached`}>
          {value.map((x) => (
            <li key={x.key} data-attached={x.name}>
              <span className="card-thumb">
                {mediaKindOf(x.type) === "video" ? <video src={previews.current.get(x.key)} muted playsInline preload="metadata" /> : <img src={previews.current.get(x.key) ?? `${fileUrl(x.key)}`} alt={x.name} />}
              </span>
              <button type="button" className="card-thumb-remove" aria-label={`Remove ${x.name}`} title="Remove" onClick={() => onChange(latest.current.filter((y) => y.key !== x.key))}>×</button>
            </li>
          ))}
          {going.map((g) => (
            <li key={g.id} aria-busy="true">
              <span className="card-thumb uploading">
                {g.video ? <video src={g.preview} muted playsInline preload="metadata" /> : <img src={g.preview} alt="" />}
                <span className="card-thumb-progress" style={{ width: `${Math.round(g.progress * 100)}%` }} />
              </span>
              <button type="button" className="card-thumb-remove" aria-label={`Cancel ${g.name}`} title="Cancel" onClick={() => g.task.cancel()}>×</button>
            </li>
          ))}
        </ul>
      )}
      {value.length + going.length < max && (
        <button type="button" id={id} className="btn btn-small card-attach" onClick={() => input.current?.click()}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21.4 11.6l-8.5 8.5a5.5 5.5 0 01-7.8-7.8l8.5-8.5a3.7 3.7 0 015.2 5.2l-8.5 8.5a1.8 1.8 0 01-2.6-2.6l7.8-7.8" /></svg>
          {value.length ? "Add more" : kinds.length === 2 ? "Attach pictures or video" : kinds[0] === "video" ? "Attach a video" : "Attach pictures"}
        </button>
      )}
      <input ref={input} type="file" hidden multiple={max > 1} accept={kinds.map((k) => `${k}/*`).join(",")} name={f.key} aria-label={inputLabel(f)} onChange={(e) => pick(e.target.files)} />
      {error && <span className="card-error" role="alert">{error}</span>}
    </div>
  );
}

function EyeIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}
